/**
 * Shared display helpers for Check #36 (Resource Loading).
 *
 * Sub-2 of 10D-2: PDF and dashboard both render the float-position pill
 * cluster, loading composition line, sorted activity table, and adaptive
 * corrective text. These helpers centralize that logic so the two surfaces
 * stay byte-identical (Amendment §A2 coherence).
 *
 * Engine emission (Sub-1, commit 6e5b1f4) provides:
 *   - check.float_position_breakdown: { critical, near_critical, floating }
 *   - check.loading_type_breakdown:   { labor_only, cost_only, dual, total_loaded }
 *   - check.severity_findings[i].float_position: 'critical'|'near_critical'|'floating'
 *
 * Display surfaces consume these verbatim — never recompute.
 *
 * @module shared/check36Display
 */

/** Band sort priority. Lower index = render first. */
export const FLOAT_POSITION_ORDER = ['critical', 'near_critical', 'floating'];

const BAND_RANK = new Map(FLOAT_POSITION_ORDER.map((band, i) => [band, i]));

/**
 * Sort findings by float-position band (critical → near_critical → floating),
 * then by taskCode ascending within band. Returns a new array; does not
 * mutate the input. Unknown bands sort to the end.
 */
export function sortFindingsByFloatPosition(findings) {
  if (!Array.isArray(findings)) return [];
  return [...findings].sort((a, b) => {
    const ra = BAND_RANK.get(a.float_position) ?? FLOAT_POSITION_ORDER.length;
    const rb = BAND_RANK.get(b.float_position) ?? FLOAT_POSITION_ORDER.length;
    if (ra !== rb) return ra - rb;
    const ca = a.taskCode || '';
    const cb = b.taskCode || '';
    return ca.localeCompare(cb);
  });
}

/**
 * Structured pill data for the float-position cluster. Returns three entries
 * while the check carries findings — bands with count===0 still render (zero
 * state is informative) but carry a `muted: true` flag so display surfaces
 * neutralize their color (PDF-POLISH-1 D11).
 * Order matches FLOAT_POSITION_ORDER.
 *
 * MEGAC-FIX F7: with ZERO findings the chip row does not render at all —
 * returns [] so both surfaces skip the cluster (an all-zero
 * "0 Critical · 0 Near-critical · 0 Floating" row carries no information;
 * passing checks render no chip row anywhere else either).
 */
export function getFloatPositionPills(check) {
  if ((check?.severity_findings || []).length === 0) return [];
  const b = check?.float_position_breakdown || { critical: 0, near_critical: 0, floating: 0 };
  return [
    { band: 'critical', label: 'Critical', count: b.critical || 0, muted: (b.critical || 0) === 0 },
    { band: 'near_critical', label: 'Near-critical', count: b.near_critical || 0, muted: (b.near_critical || 0) === 0 },
    { band: 'floating', label: 'Floating', count: b.floating || 0, muted: (b.floating || 0) === 0 },
  ];
}

/**
 * Loading composition line for the loaded set.
 *
 *   total_loaded === 0, findings > 0 → "Loaded: none — see activity table for unloaded set"
 *   total_loaded === 0, findings = 0 → "Loaded: none — no resource-assignable activities in scope"
 *                                      (G-2: never point at a table that cannot exist)
 *   otherwise           → "Loaded: X Labour only · Y Cost only · Z Dual"
 *                         (terms with count===0 are omitted)
 */
export function formatLoadingComposition(check) {
  const b = check?.loading_type_breakdown || { labor_only: 0, cost_only: 0, dual: 0, total_loaded: 0 };
  if (!b.total_loaded || b.total_loaded === 0) {
    const hasUnloadedRows = (check?.severity_findings || []).length > 0;
    return hasUnloadedRows
      ? 'Loaded: none \u2014 see activity table for unloaded set'
      : 'Loaded: none \u2014 no resource-assignable activities in scope';
  }
  const parts = [];
  // MEGAC-FIX F15: BrE for Anglicisable common nouns ("Labour"); the canonical
  // labor_only field key is P6/engine vocabulary and stays untouched.
  if (b.labor_only > 0) parts.push(`${b.labor_only} Labour only`);
  if (b.cost_only > 0) parts.push(`${b.cost_only} Cost only`);
  if (b.dual > 0) parts.push(`${b.dual} Dual`);
  return `Loaded: ${parts.join(' \u00b7 ')}`;
}

/**
 * Adaptive corrective-action text. Branches on K/M/L composition so the
 * recommendation tracks the actual unloaded shape.
 *
 *   K > 0           → mentions critical-path priority
 *   M > 0           → mentions near-critical
 *   L > 0           → mentions positive-float deferral
 *   total === 0     → returns null (display layer falls back to neutral text)
 *
 * Wording uses "is/are" and "this/those" agreement for singular cases so the
 * sentence reads naturally for K=1 or M=1 or L=1.
 *
 * Spec v2 §15 Mode Selector — mode changes the imperative voice on the K>0
 * priority clause; K=0 branches are descriptive ("are near-critical", "have
 * positive float") and read identically across modes.
 *
 *   GENERAL    → "… fix this/those first."
 *   CONSULTANT → "… have the contractor fix this/those first."
 *   CONTRACTOR → "… ensure this is/those are fixed first."
 *
 * @param {object} check
 * @param {string} [mode='general'] - 'general' | 'consultant' | 'contractor'
 */
export function buildResourceLoadingAction(check, mode = 'general') {
  const b = check?.float_position_breakdown || { critical: 0, near_critical: 0, floating: 0 };
  const K = b.critical || 0;
  const M = b.near_critical || 0;
  const L = b.floating || 0;
  const total = K + M + L;
  if (total === 0) return null;

  // F4 (D-Check36AllFloatingProse, v3.36): when nothing is critical or
  // near-critical (only positive-float activities unloaded), the "Of N unloaded
  // activities, The N have positive float…" shape read with a stray capital
  // "The" mid-sentence. Emit a standalone sentence with no "Of N," lead.
  if (K === 0 && M === 0) {
    return `All ${L} unloaded activit${L === 1 ? 'y has' : 'ies have'} positive float and can be addressed after the schedule recovers.`;
  }

  const parts = [`Of ${total} unloaded activit${total === 1 ? 'y' : 'ies'},`];

  if (K > 0) {
    const subject = K === 1 ? 'this' : 'those';
    let priorityClause;
    switch (mode) {
      case 'contractor':
        priorityClause = `ensure ${K === 1 ? 'this is' : 'those are'} fixed first`;
        break;
      case 'consultant':
        priorityClause = `have the contractor fix ${subject} first`;
        break;
      case 'general':
      default:
        priorityClause = `fix ${subject} first`;
        break;
    }
    parts.push(
      `${K} ${K === 1 ? 'is' : 'are'} on the critical path \u2014 ${priorityClause}.`,
    );
  }
  if (M > 0) {
    // F4 sibling (v3.36 B2): when K = 0 the near-critical clause is the first
    // after the "Of N," lead, so it must NOT open with a stray capital "The"
    // ("Of 3 unloaded activities, 3 are near-critical\u2026"). When K > 0 it follows
    // the critical clause as "The next M \u2026" \u2014 that shape is unchanged.
    const lead = K > 0 ? 'The next ' : '';
    parts.push(`${lead}${M} ${M === 1 ? 'is' : 'are'} near-critical (1\u201310 days float).`);
  }
  if (L > 0) {
    const prefix = K > 0 || M > 0 ? 'The remaining' : 'The';
    parts.push(
      `${prefix} ${L} ${L === 1 ? 'has' : 'have'} positive float and can be addressed after the schedule recovers.`,
    );
  }
  return parts.join(' ');
}

/**
 * Adaptive Top Issues card subtitle. Surfaces the most actionable bucket
 * (critical-path first, then near-critical, then floating-only).
 *
 *   K > 0           → "Resource loading at P% — K critical-path activities lack resources"
 *   M > 0  (K = 0)  → "Resource loading at P% — 0 critical-path, M near-critical activities lack resources"
 *   only L (K=M=0)  → "Resource loading at P% — L activities lack resources (all floating)"
 *
 * Returns null when total unloaded === 0 (caller falls back to a neutral
 * "fully loaded" string — but Check #36 should not appear in Top Issues at
 * that point, since it would be passing).
 */
export function buildResourceLoadingSubtitle(check) {
  const b = check?.float_position_breakdown || { critical: 0, near_critical: 0, floating: 0 };
  const K = b.critical || 0;
  const M = b.near_critical || 0;
  const L = b.floating || 0;
  if (K + M + L === 0) return null;

  const pct = typeof check?.ratio_pct === 'number' ? check.ratio_pct.toFixed(1) : '0.0';

  if (K > 0) {
    return `Resource Loading at ${pct}% \u2014 ${K} critical-path ${K === 1 ? 'activity' : 'activities'} lack${K === 1 ? 's' : ''} resources`;
  }
  if (M > 0) {
    return `Resource Loading at ${pct}% \u2014 0 critical-path, ${M} near-critical ${M === 1 ? 'activity' : 'activities'} lack${M === 1 ? 's' : ''} resources`;
  }
  return `Resource Loading at ${pct}% \u2014 ${L} ${L === 1 ? 'activity' : 'activities'} lack${L === 1 ? 's' : ''} resources (all floating)`;
}
